/**
=========================================================
* Trust Cargo - add dialog
=========================================================

* Every "add" happens in a dialog over the current page, so you never lose
* your place - your filters, your page number and your scroll position are
* all still there when it closes.
*
* On success it calls onCreated(), which is how the list behind it refreshes
* without a navigation.
*/

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import entities from "components/AddDialog/entities";

/**
 * Shared field sizing. Kept in one place so the text inputs, the date
 * pickers and the search boxes all end up the same height - mixing MUI
 * densities is what makes a form look thrown together.
 */
const FIELD_SX = {
  "& .MuiInputBase-root": {
    minHeight: 52,
    fontSize: 15,
  },
  "& .MuiInputBase-input": {
    padding: "14px 14px",
  },
  "& .MuiFormHelperText-root": {
    marginTop: 2,
    fontSize: 12,
  },
};

function AddDialog({ open, entity, onClose, onCreated, recordId, record, defaults }) {
  const config = entities[entity];
  const isEdit = Boolean(recordId);

  const [values, setValues] = useState({});
  const [options, setOptions] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [serverError, setServerError] = useState("");
  // why the dropdowns are empty, when they are
  const [optionsError, setOptionsError] = useState("");

  // start clean every time it opens, and pull any dropdown choices
  useEffect(() => {
    if (!open || !config) return;

    /**
     * Starting values for a new record, in order of precedence:
     *   config.defaults - constants the API needs but nobody types
     *   defaults prop   - what the calling screen already knows
     */
    setValues({ ...(config.defaults || {}), ...(defaults || {}) });
    setErrors({});
    setServerError("");

    // drop nulls so inputs stay controlled with ""
    const prefill = (rec) => {
      const clean = {};
      Object.keys(rec).forEach((k) => {
        clean[k] = rec[k] === null ? "" : rec[k];
      });
      setValues(clean);
    };

    /**
     * The row is often already in hand from the list that opened this dialog.
     * Passing it in skips a round trip - and is the only way to edit a post,
     * whose show endpoint is broken on the server.
     */
    if (recordId && record) {
      prefill(record);
    } else if (recordId) {
      /**
       * Two conventions live here. The v2 endpoints are REST-shaped
       * (GET /orders/5) and return the record directly. The legacy ones are
       * GET /specefic_user/5 and wrap it as {user: {...}}. `config.edit`
       * describes the legacy case; without it the REST shape is assumed.
       */
      const url = config.edit?.show ? config.edit.show(recordId) : `${config.endpoint}/${recordId}`;

      fetch(url, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          const rec = config.edit?.unwrap ? json?.[config.edit.unwrap] : json;

          if (!rec) {
            setServerError("Could not load the record");
            return;
          }
          prefill(rec);
        })
        .catch(() => setServerError("Could not load the record"));
    }

    const selects = config.fields.filter((f) => f.optionsFrom);
    if (selects.length === 0) return;

    setLoadingOptions(true);
    setOptionsError("");

    /**
     * Every failure here used to be swallowed into an empty array, so a dead
     * endpoint looked exactly like a legitimately empty dropdown. Now the
     * reason is captured and shown.
     *
     * Requests are made once per URL rather than once per field - the order
     * form has three selects that all read the same endpoint.
     */
    const urls = [...new Set(selects.map((f) => f.optionsFrom.url))];

    Promise.all(
      urls.map(async (url) => {
        try {
          const res = await fetch(url, { cache: "no-store" });
          if (!res.ok) {
            return [url, null, `HTTP ${res.status}`];
          }
          return [url, await res.json(), null];
        } catch (e) {
          return [url, null, e?.message || "network error"];
        }
      })
    ).then((results) => {
      const byUrl = {};
      const problems = [];

      results.forEach(([url, json, err]) => {
        byUrl[url] = json;
        if (err) problems.push(`${url.split("/").pop()}: ${err}`);
      });

      const next = {};
      selects.forEach((f) => {
        const json = byUrl[f.optionsFrom.url];
        if (!json) {
          next[f.name] = [];
          return;
        }

        const list = f.optionsFrom.path ? json[f.optionsFrom.path] : json;

        if (!Array.isArray(list)) {
          // the endpoint answered but not in the shape this field expects
          problems.push(`${f.label}: no "${f.optionsFrom.path}" in response`);
          next[f.name] = [];
          return;
        }

        next[f.name] = list;
      });

      setOptions(next);
      setOptionsError(problems.join(" · "));
      setLoadingOptions(false);
    });
  }, [open, entity, recordId]);

  if (!config) return null;

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  /**
   * A choice can prefill another field from the option that was picked -
   * choosing a customer fills in the next free shop code for them.
   * The target stays editable; this is a suggestion, not a lock.
   */
  const applyFills = (field, chosen) => {
    if (!field.fills || !chosen) return;

    const { target, from, compute } = field.fills;
    // `compute` lets the entity work the value out from the whole option
    // rather than reading a single key off it
    const value = compute ? compute(chosen) : chosen[from];

    setValues((v) => ({ ...v, [target]: value ?? "" }));
    setErrors((e) => ({ ...e, [target]: undefined }));
  };

  const validate = () => {
    const found = {};
    config.fields.forEach((f) => {
      // a password is required to create an account but blank on edit means
      // "leave it as it is" - demanding it would force a reset every time
      if (isEdit && f.optionalOnEdit) return;

      if (f.required && !String(values[f.name] ?? "").trim()) {
        found[f.name] = "Required";
      }
    });
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    setServerError("");

    try {
      const e = config.edit || {};
      // legacy endpoints want the id in the body; v2 ones carry it in the URL
      const withId = isEdit && e.sendId;

      let payload = values;
      let headers = { Accept: "application/json", "Content-Type": "application/json" };

      // forms carrying files must go up as multipart; the API expects the
      // repeated-index shape image[0], image[1] ...
      if (config.multipart) {
        const fd = new FormData();
        config.fields.forEach((f) => {
          const v = values[f.name];
          if (v === undefined || v === null || v === "") return;

          if (f.type === "file") {
            Array.from(v).forEach((file, i) => fd.append(`${f.name}[${i}]`, file));
          } else {
            fd.append(f.name, v);
          }
        });
        if (withId) fd.append("id", recordId);
        payload = fd;
        headers = { Accept: "application/json" };
      } else if (withId) {
        payload = { ...values, id: recordId };
      }

      if (isEdit) {
        // `submit` may be a fixed URL or built from the id (edit_post/{id})
        let url = `${config.endpoint}/${recordId}`;
        if (typeof e.submit === "function") url = e.submit(recordId);
        else if (e.submit) url = e.submit;

        if ((e.method || "put").toLowerCase() === "post") {
          await axios.post(url, payload, { headers });
        } else {
          await axios.put(url, payload, { headers });
        }
      } else {
        const created = await axios.post(config.endpoint, payload, { headers });

        // entity-specific follow-up work, e.g. an order leaving a supplier of
        // the same shop code behind. It must never turn a saved record into a
        // visible failure, so its own errors are its business.
        if (config.afterCreate) {
          await config.afterCreate(values, created?.data);
        }
      }
      setSaving(false);
      onCreated();
      onClose();
    } catch (err) {
      setSaving(false);
      // surface the API's own message rather than a generic failure
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not save";
      setServerError(String(msg));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      // the order form has 15 fields; a wider sheet keeps two roomy columns
      // instead of two cramped ones
      maxWidth={config.wide ? "lg" : "sm"}
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiTypography variant="h5" fontWeight="bold">
          {isEdit ? `Edit - ${config.title.replace(/^(Add|Create)\s*/, "")}` : config.title}
        </SuiTypography>
        <Icon
          onClick={saving ? undefined : onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: saving ? "default" : "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loadingOptions ? (
          <CargoLoader label="Loading options" size={70} />
        ) : (
          <SuiBox
            pt={1}
            sx={{
              display: "grid",
              // two columns on the wide (order) form, one everywhere else
              gridTemplateColumns: config.wide ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
              columnGap: 3,
              rowGap: 2,
              // full-width rows for the error banner and any long text area
              "& .full-span": { gridColumn: "1 / -1" },
            }}
          >
            {serverError && (
              <SuiBox className="full-span" mb={1}>
                <Alert severity="error">{serverError}</Alert>
              </SuiBox>
            )}

            {optionsError && (
              <SuiBox className="full-span" mb={1}>
                <Alert severity="warning">{`Could not load lists - ${optionsError}`}</Alert>
              </SuiBox>
            )}

            {config.fields.map((f) => {
              const common = {
                key: f.name,
                // multiline fields (NOTE, message body) take the full width
                className: f.multiline ? "full-span" : undefined,
                label: f.label,
                value: values[f.name] ?? "",
                onChange: (e) => setField(f.name, e.target.value),
                error: Boolean(errors[f.name]),
                helperText: errors[f.name] || " ",
                fullWidth: true,
                // full-size inputs: these get read and typed into all day, so
                // the compact "small" density was working against the user
                size: "medium",
                InputLabelProps: { shrink: true, sx: { fontSize: 15 } },
                sx: FIELD_SX,
              };

              if (f.type === "select") {
                const list = options[f.name] || [];
                const valueKey = f.optionsFrom.value;
                const labelKey = f.optionsFrom.label;

                // the option currently held, found back from the stored id
                const selected =
                  list.find((o) => String(o[valueKey]) === String(values[f.name])) || null;

                /**
                 * A searchable combobox, not a plain dropdown. With 150
                 * customers a select means scrolling a wall of names; here you
                 * type "AKT" or part of the name and it narrows as you go.
                 */
                return (
                  <Autocomplete
                    key={f.name}
                    className={f.multiline ? "full-span" : undefined}
                    options={list}
                    value={selected}
                    size="medium"
                    fullWidth
                    autoHighlight
                    openOnFocus
                    sx={FIELD_SX}
                    // roomier rows in the dropdown, and a taller list before
                    // it starts scrolling
                    ListboxProps={{ style: { maxHeight: 320, fontSize: 15 } }}
                    noOptionsText="No results"
                    loadingText="Loading..."
                    getOptionLabel={(o) => String(o?.[labelKey] ?? "")}
                    isOptionEqualToValue={(o, v) => String(o[valueKey]) === String(v?.[valueKey])}
                    onChange={(e, chosen) => {
                      setField(f.name, chosen ? chosen[valueKey] : "");
                      applyFills(f, chosen);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.label}
                        error={Boolean(errors[f.name])}
                        helperText={errors[f.name] || " "}
                        InputLabelProps={{ shrink: true, sx: { fontSize: 15 } }}
                        placeholder={list.length ? "Type to search..." : "No options"}
                      />
                    )}
                  />
                );
              }

              if (f.type === "file") {
                const picked = values[f.name] ? Array.from(values[f.name]) : [];
                return (
                  <SuiBox key={f.name} display="flex" flexDirection="column" gap={0.75}>
                    <SuiTypography variant="caption" fontWeight="bold">
                      {f.label}
                    </SuiTypography>
                    <input
                      type="file"
                      multiple={Boolean(f.multiple)}
                      accept="image/*"
                      onChange={(e) => setField(f.name, e.target.files)}
                      style={{ fontFamily: "inherit", fontSize: 13 }}
                    />
                    {picked.length > 0 && (
                      <SuiTypography variant="caption" sx={{ opacity: 0.7 }}>
                        {`${picked.length} file(s) selected`}
                      </SuiTypography>
                    )}
                  </SuiBox>
                );
              }

              const inputType = ["password", "date", "number"].includes(f.type) ? f.type : "text";

              return (
                <TextField
                  {...common}
                  type={inputType}
                  inputProps={f.type === "number" ? { step: "any" } : undefined}
                  multiline={Boolean(f.multiline)}
                  minRows={f.multiline ? 3 : undefined}
                />
              );
            })}
          </SuiBox>
        )}
      </DialogContent>

      {/* one clear primary action in gold, and a cancel that stays out of
          its way - the two used to look like the same weight */}
      <DialogActions
        sx={({ palette, functions: { rgba } }) => ({
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1.5,
          borderTop: `1px solid ${rgba(palette.dark.main, 0.07)}`,
        })}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          size="large"
          sx={({ palette, functions: { rgba } }) => ({
            color: rgba(palette.dark.main, 0.6),
            minWidth: 120,
            fontSize: 15,
            "&:hover": { backgroundColor: rgba(palette.dark.main, 0.05) },
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={submit}
          disabled={saving || loadingOptions}
          sx={{ minWidth: 170, fontSize: 15 }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddDialog.defaultProps = {
  onCreated: () => {},
  recordId: null,
  record: null,
  defaults: null,
};

AddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.oneOf(Object.keys(entities)).isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  // when set, the dialog loads that record and updates instead of creating
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // optional: the row itself, used to prefill without re-fetching it
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
  // optional: starting values for a new record
  // eslint-disable-next-line react/forbid-prop-types
  defaults: PropTypes.object,
};

export default AddDialog;
